import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useSearchParams } from 'react-router-dom'
import { Loader2, History, Film, BadgePercent, Search, SlidersHorizontal, X } from 'lucide-react'
import { useArchiveProducts } from '@/hooks/useProducts'
import { ProductGrid } from '@/components/product-grid'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { Product } from '@/types/database'
import type { ArchiveSort, PriceBand } from '@/types/product-ui'
import { getPageTitle } from '@/lib/branding'

const SORT_OPTIONS: Array<{ label: string; value: ArchiveSort }> = [
  { label: 'Newest', value: 'newest' },
  { label: 'Price: Low to High', value: 'price-asc' },
  { label: 'Price: High to Low', value: 'price-desc' },
  { label: 'Biggest Savings', value: 'biggest-savings' },
]

const PRICE_BAND_OPTIONS: Array<{ label: string; value: PriceBand }> = [
  { label: 'Under $100', value: 'under-100' },
  { label: '$100 - $250', value: '100-250' },
  { label: '$250+', value: '250-plus' },
]

function isValidSort(value: string | null): value is ArchiveSort {
  return !!value && SORT_OPTIONS.some(option => option.value === value)
}

function isValidPriceBand(value: string): value is PriceBand {
  return PRICE_BAND_OPTIONS.some(option => option.value === value)
}

function getArchivePrice(product: Product) {
  return product.original_price * (1 - product.archive_discount_percent / 100)
}

function getSavings(product: Product) {
  return product.original_price - getArchivePrice(product)
}

function matchesPriceBand(price: number, band: PriceBand) {
  if (band === 'under-100') return price < 100
  if (band === '100-250') return price >= 100 && price <= 250
  return price > 250
}

export function ArchivePage() {
  const { products, isLoading, error } = useArchiveProducts()
  const [searchParams, setSearchParams] = useSearchParams()

  const query = searchParams.get('q') || ''
  const sort: ArchiveSort = isValidSort(searchParams.get('sort')) ? (searchParams.get('sort') as ArchiveSort) : 'newest'
  const selectedPriceBands = useMemo(
    () => (searchParams.get('price') || '')
      .split(',')
      .filter(isValidPriceBand),
    [searchParams]
  )
  const minDiscount = searchParams.get('minDiscount') === '20' ? 20 : undefined
  const hasActiveFilters = query.length > 0 || selectedPriceBands.length > 0 || !!minDiscount || sort !== 'newest'

  const updateParams = (updates: {
    query?: string
    sort?: ArchiveSort
    priceBands?: PriceBand[]
    minDiscount?: number
  }) => {
    const next = new URLSearchParams(searchParams)

    const nextQuery = updates.query ?? query
    const nextSort = updates.sort ?? sort
    const nextBands = updates.priceBands ?? selectedPriceBands
    const nextMinDiscount = updates.minDiscount !== undefined ? updates.minDiscount : minDiscount

    if (nextQuery) next.set('q', nextQuery)
    else next.delete('q')

    if (nextSort !== 'newest') next.set('sort', nextSort)
    else next.delete('sort')

    if (nextBands.length > 0) next.set('price', nextBands.join(','))
    else next.delete('price')

    if (nextMinDiscount) next.set('minDiscount', String(nextMinDiscount))
    else next.delete('minDiscount')

    setSearchParams(next, { replace: true })
  }

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    const filtered = products.filter(product => {
      const archivePrice = getArchivePrice(product)
      const matchesQuery = normalizedQuery.length === 0 || [
        product.title,
        product.slug,
        product.short_description || '',
      ].some(value => value.toLowerCase().includes(normalizedQuery))

      const matchesBand = selectedPriceBands.length === 0
        || selectedPriceBands.some(band => matchesPriceBand(archivePrice, band))

      const matchesDiscount = !minDiscount || product.archive_discount_percent >= minDiscount

      return matchesQuery && matchesBand && matchesDiscount
    })

    return filtered.sort((a, b) => {
      if (sort === 'price-asc') return getArchivePrice(a) - getArchivePrice(b)
      if (sort === 'price-desc') return getArchivePrice(b) - getArchivePrice(a)
      if (sort === 'biggest-savings') return getSavings(b) - getSavings(a)

      const aTime = new Date(a.featured_end_date || a.created_at).getTime()
      const bTime = new Date(b.featured_end_date || b.created_at).getTime()
      return bTime - aTime
    })
  }, [products, query, selectedPriceBands, minDiscount, sort])

  const clearFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }

  const togglePriceBand = (band: PriceBand) => {
    const nextBands = selectedPriceBands.includes(band)
      ? selectedPriceBands.filter(value => value !== band)
      : [...selectedPriceBands, band]

    updateParams({ priceBands: nextBands })
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-red-400">Error loading past reviews</p>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Past Reviews')}</title>
        <meta
          name="description"
          content="Browse past video production reviews with ongoing discounted pricing and full watch-before-you-buy breakdowns."
        />
      </Helmet>

      <div className="flex flex-1 flex-col bg-zinc-50">
        <section className="relative overflow-hidden border-b border-zinc-200 py-12">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-zinc-100 to-transparent" />
            <div className="absolute -left-24 top-8 h-56 w-56 rounded-full bg-red-100/50 blur-3xl" />
            <div className="absolute -right-24 bottom-8 h-56 w-56 rounded-full bg-zinc-200/70 blur-3xl" />
          </div>

          <div className="container relative mx-auto px-4 text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-zinc-300/90 bg-white/90 px-4 py-1.5 text-sm font-medium text-zinc-700">
              <History className="size-4" />
              Past Reviews
            </span>
            <h1 className="mb-4 text-4xl font-bold text-zinc-900 md:text-5xl">Every Review, Still Available</h1>
            <p className="mx-auto max-w-2xl text-lg text-zinc-600">
              Watch the full breakdown, compare pricing clearly, and buy when it fits your setup.
            </p>
          </div>
        </section>

        <section className="flex flex-1 flex-col py-10">
          <div className="container mx-auto w-full px-4">
            {products.length === 0 ? (
              <div className="mx-auto w-full max-w-2xl rounded-3xl border border-zinc-200/80 bg-white/90 p-8 text-center shadow-sm backdrop-blur md:p-12">
                <History className="mx-auto mb-4 size-12 text-zinc-300" />
                <h2 className="mb-2 text-2xl font-semibold text-zinc-800">Past reviews are coming soon</h2>
                <p className="mx-auto mb-7 max-w-xl text-zinc-600">
                  Once the first featured drop completes, every review will appear here with a discounted price and full walkthrough.
                </p>
                <Button asChild size="lg" className="bg-red-600 text-white hover:bg-red-700">
                  <Link to="/">View This Week&apos;s Deal</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-6 grid gap-4 rounded-2xl border border-zinc-200 bg-white p-5 md:grid-cols-3">
                  <div className="flex items-start gap-3">
                    <History className="mt-0.5 size-5 text-red-600" />
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">
                        {products.length} {products.length === 1 ? 'review' : 'reviews'} live
                      </p>
                      <p className="text-sm text-zinc-500">Permanent discounted access.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Film className="mt-0.5 size-5 text-red-600" />
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">Watch before you buy</p>
                      <p className="text-sm text-zinc-500">Every listing links to a full review.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgePercent className="mt-0.5 size-5 text-red-600" />
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">Price clarity</p>
                      <p className="text-sm text-zinc-500">Clear now/was/savings on every card.</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex w-full items-center gap-2 lg:max-w-md">
                      <Search className="size-4 text-zinc-400" />
                      <Input
                        value={query}
                        onChange={(event) => updateParams({ query: event.target.value })}
                        placeholder="Search by product name..."
                        className="h-10"
                        aria-label="Search past reviews"
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2 py-1.5">
                        <SlidersHorizontal className="size-4 text-zinc-500" />
                        <select
                          value={sort}
                          onChange={event => updateParams({ sort: event.target.value as ArchiveSort })}
                          className="bg-transparent text-sm font-medium text-zinc-700 focus:outline-none"
                          aria-label="Sort past reviews"
                        >
                          {SORT_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {PRICE_BAND_OPTIONS.map(option => {
                        const isActive = selectedPriceBands.includes(option.value)
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => togglePriceBand(option.value)}
                            className={isActive
                              ? 'rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600'
                              : 'rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-zinc-300 hover:text-zinc-900'
                            }
                          >
                            {option.label}
                          </button>
                        )
                      })}

                      <button
                        type="button"
                        onClick={() => updateParams({ minDiscount: minDiscount ? undefined : 20 })}
                        className={minDiscount
                          ? 'rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600'
                          : 'rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-zinc-300 hover:text-zinc-900'
                        }
                      >
                        20%+ Off
                      </button>

                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 hover:border-zinc-300 hover:text-zinc-900"
                        >
                          <X className="size-3.5" />
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-zinc-500">
                    Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'}
                  </p>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-14 text-center">
                    <h2 className="text-xl font-semibold text-zinc-900">No matching reviews found</h2>
                    <p className="mt-2 text-sm text-zinc-500">
                      Adjust your filters or clear them to see all archived products.
                    </p>
                    <div className="mt-6">
                      <Button onClick={clearFilters} variant="outline">Reset Filters</Button>
                    </div>
                  </div>
                ) : (
                  <ProductGrid
                    products={filteredProducts}
                    variant="archive-default"
                    className="xl:grid-cols-4"
                  />
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  )
}
